import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { BiBullseye, BiPlus } from 'react-icons/bi';

import { API_BASE_URL } from '../utils/urls.js';
// Import the new modal component (ensure path is correct)
import GPSCheckInModal from './GPSCheckInModal'; 

// Helper function to extract unique case data from tasks array (kept original)
const getUniqueCasesFromTasks = (tasks, existingCases = []) => {
    const caseMap = new Map();
    existingCases.forEach(c => caseMap.set(c.id, c));
    tasks.forEach(task => {
        const caseId = task.case_id;
        if (!caseMap.has(caseId)) {
            caseMap.set(caseId, {
                id: caseId,
                case_name: task.case_name || `Case ID: ${caseId}`,
                status: 'Task Assigned',
                client_name: 'N/A', 
                due_date: task.end_time,
                isTaskBased: true,
            });
        } else if (caseMap.get(caseId).isTaskBased) {
             const currentDueDate = new Date(caseMap.get(caseId).due_date);
             const newDueDate = new Date(task.end_time);
             if (newDueDate < currentDueDate) {
                 caseMap.get(caseId).due_date = task.end_time;
             }
        }
    });
    return Array.from(caseMap.values());
};


const FieldAgentDashboard = () => {
    const [cases, setCases] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showCheckInModal, setShowCheckInModal] = useState(false); // NEW STATE for modal
    
    const user = JSON.parse(localStorage.getItem('user'));
    const userId = user?.id;

    const fetchData = async () => {
        const token = localStorage.getItem('token');
        if (!token || !userId) {
            setError('Authentication token or user ID not found.');
            setLoading(false);
            return;
        }

        try {
            const [casesRes, tasksRes] = await Promise.all([
                axios.get(`${API_BASE_URL}/agent/my-cases`, {
                    headers: { Authorization: `Bearer ${token}` }
                }),
                axios.get(`${API_BASE_URL}/tasks/user/${userId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                }),
            ]);

            const fetchedCases = casesRes.data.data || [];
            const fetchedTasks = tasksRes.data || [];
            const combinedCases = getUniqueCasesFromTasks(fetchedTasks, fetchedCases);
            console.log(fetchedCases)
            setCases(combinedCases);
            setTasks(fetchedTasks);

        } catch (err) {
            console.error('Error fetching dashboard data:', err);
            setError('Failed to load dashboard data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [userId]);
    
    const handleCheckInSuccess = () => {
        // Option to refresh the activity log on the dashboard if necessary
        // For simplicity, we just close the modal and trust the submission was logged.
        setShowCheckInModal(false);
        // You might want to update a small 'Last Check-In Time' state here.
    };

    if (loading) {
        return <div className="p-6 text-center">Loading dashboard...</div>;
    }

    if (error) {
        return <div className="p-6 text-center text-red-500">{error}</div>;
    }

    // Prepare table row components
    const CaseRow = ({ caseItem }) => (
        <tr key={caseItem.id} className="hover:bg-gray-50">
            <td className={`px-6 py-4 whitespace-nowrap ${caseItem.isTaskBased ? 'font-semibold text-purple-600' : 'text-gray-900'}`}>
                <Link to={`/agent/cases/pi/${caseItem.id}`} className="text-blue-600 hover:underline">
                    {caseItem.case_name}
                </Link>
                {caseItem.isTaskBased && <span className="ml-2 text-xs font-normal text-purple-500">(Task-Assigned)</span>}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{caseItem.status}</td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{caseItem.client_name || 'N/A'}</td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{caseItem.assigned_to_names || 'N/A'}</td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{caseItem.fieldwork_status || 'N/A'}</td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {caseItem.due_date ? new Date(caseItem.due_date).toLocaleDateString() : 'N/A'}
            </td>
        </tr>
    );


    return (
        <div className="p-6 space-y-6">
            <h1 className="text-3xl font-bold text-gray-800">Field Agent Dashboard</h1>

            {/* NEW: Check-in Button with Better UI */}
            {/* <div className="flex justify-between items-center bg-indigo-50 p-4 rounded-lg shadow-md border border-indigo-200">
                <p className="text-lg font-medium text-indigo-800 flex items-center">
                    <BiBullseye className='w-6 h-6 mr-3 text-indigo-600'/> 
                    Ready for your shift? Record your current location.
                </p>
                <button
                    onClick={() => setShowCheckInModal(true)}
                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg shadow-md hover:bg-indigo-700 transition duration-300 flex items-center space-x-1"
                >
                    <BiBullseye className='w-4 h-4'/> <span>GPS Check-In</span>
                </button>
            </div> */}
            
            {/* My Assigned Cases Section */}
            <div className="bg-white p-6 rounded-lg shadow-sm">
                <h2 className="text-xl font-bold mb-4 text-gray-800">My Assigned Cases</h2>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Case Name</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned To</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">FW Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {cases.length > 0 ? (
                                cases.map(caseItem => <CaseRow key={caseItem.id} caseItem={caseItem} />)
                            ) : (
                                <tr><td colSpan="4" className="px-6 py-4 text-center text-gray-500">No cases found.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* My Upcoming Tasks Section */}
            <div className="bg-white p-6 rounded-lg shadow-sm">
                <h2 className="text-xl font-bold mb-4 text-gray-800">My Upcoming Tasks</h2>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Task Title</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Case</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {tasks.length > 0 ? (
                                tasks.map(task => (
                                    <tr key={task.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{task.task_title}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            <Link to={`/cases/${task.case_id}`} className="text-blue-600 hover:underline">
                                                {task.case_name || `Case ID: ${task.case_id}`}
                                            </Link>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(task.end_time).toLocaleDateString()}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr><td colSpan="3" className="px-6 py-4 text-center text-gray-500">No tasks found.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Render the Modal */}
            {showCheckInModal && (
                <GPSCheckInModal
                    userId={userId}
                    onClose={() => setShowCheckInModal(false)}
                    onCheckInSuccess={handleCheckInSuccess}
                />
            )}
        </div>
    );
};

export default FieldAgentDashboard;